import { forwardRef, useMemo } from "react";
import {
  DiffPercentageBadge,
  formatCurrency,
  formatNumber,
  Skeleton,
} from "ui";
import { ChartCard } from "../../ChartCard";
import type { ErrorResponse } from "../../../../../hooks/useMoneyHashQuery";

type ValueFormat = "currency" | "percentage" | "number";

type MetricCardProps = {
  title: string;
  subTitle?: string;
  description?: string;
  value: number;
  /** How the metric value is formatted. Defaults to "number". */
  format?: ValueFormat;
  /** Percentage change vs the previous period. Hidden when null/undefined. */
  diffPercentage?: number | null;
  className?: string;
  contentClassName?: string;
  headerAction?: React.ReactNode;
  queryStatus?: "loading" | "error" | "success";
  error?: ErrorResponse | null;
  retry?: () => void;
};

function formatValue(value: number, format: ValueFormat) {
  if (format === "currency")
    return formatCurrency({ amount: value, currency: "USD", compact: true });
  if (format === "percentage") return `${formatNumber({ value })}%`;
  return formatNumber({ value, compact: true });
}

export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  function MetricCard(
    {
      title,
      subTitle,
      description,
      value,
      format = "number",
      diffPercentage,
      className,
      contentClassName,
      headerAction,
      queryStatus,
      error,
      retry,
    },
    ref
  ) {
    const displayedValue = useMemo(
      () => formatValue(value, format),
      [value, format]
    );

    return (
      <ChartCard
        ref={ref}
        title={title}
        subTitle={subTitle}
        description={description}
        className={className}
        contentClassName={contentClassName ?? "h-auto flex-1 justify-center"}
        headerAction={headerAction}
        queryStatus={queryStatus}
        error={error}
        retry={retry}
        loadingSkeleton={
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-6">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-6 w-20" />
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center gap-3 py-6">
          <span className="text-4xl font-bold text-foundations-boldest">
            {displayedValue}
          </span>
          {diffPercentage != null && (
            <DiffPercentageBadge
              diffPercentage={diffPercentage}
              variant={diffPercentage >= 0 ? "success" : "danger"}
            />
          )}
        </div>
      </ChartCard>
    );
  }
);
